import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Flame,
  Target,
  Users,
  Shield,
  BarChart3,
  Lock,
  Crown,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => {
  const val = n || 0;
  return '\u20a6' + val.toLocaleString();
};

function VelocityChart({ velocity }) {
  const amounts = velocity.map((v) => v.amount);
  const maxVal = Math.max(...amounts, 1);
  return (
    <Card className="border-0 shadow-sm mb-4" data-testid="velocity-chart">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-700">Savings Velocity</p>
          <Badge className="bg-amber-100 text-amber-700 text-[10px]">Premium</Badge>
        </div>
        <div className="flex items-end gap-2 h-28">
          {velocity.map((w) => {
            const pct = Math.max((w.amount / maxVal) * 100, 4);
            return (
              <div key={w.week} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-lg bg-slate-100 relative" style={{ height: '96px' }}>
                  <div
                    className="absolute bottom-0 w-full rounded-lg bg-gradient-to-t from-[#0B1F3A] to-[#2272C5] transition-all duration-500"
                    style={{ height: pct + '%' }}
                  />
                </div>
                <span className="text-[9px] text-slate-500 font-medium">{w.week}</span>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-center">Last 4 weeks contribution activity</p>
      </CardContent>
    </Card>
  );
}

function LockedSection({ title, navigate: nav }) {
  return (
    <Card className="border-0 shadow-sm mb-4 opacity-60">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-slate-700">{title}</p>
          <Lock size={14} className="text-slate-400" />
        </div>
        <div className="space-y-2 blur-sm">
          <div className="h-6 bg-slate-200 rounded w-3/4" />
          <div className="h-6 bg-slate-200 rounded w-1/2" />
        </div>
        <div className="text-center mt-3">
          <Button
            variant="outline"
            size="sm"
            className="text-xs rounded-full"
            onClick={() => nav('/pricing')}
          >
            Upgrade to unlock
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FlowCard({ ratio }) {
  const savedAmt = fmt(ratio.saved);
  const receivedAmt = fmt(ratio.received);
  const netAmt = fmt(ratio.net_flow);
  const isPositive = ratio.net_flow >= 0;
  const sign = isPositive ? '+' : '';

  return (
    <Card className="border-0 shadow-sm mb-4" data-testid="money-flow-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-700">Money Flow</p>
          <Badge className="bg-violet-100 text-violet-700 text-[10px]">Insights</Badge>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowUpRight size={16} className="text-red-500" />
              <span className="text-sm text-slate-600">Money Out</span>
            </div>
            <span className="text-sm font-bold text-slate-900">{savedAmt}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowDownRight size={16} className="text-emerald-500" />
              <span className="text-sm text-slate-600">Money In</span>
            </div>
            <span className="text-sm font-bold text-emerald-600">{receivedAmt}</span>
          </div>
          <div className="h-px bg-slate-100" />
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-700">Net Flow</span>
            <span className={'text-sm font-bold ' + (isPositive ? 'text-emerald-600' : 'text-red-500')}>
              {sign}{netAmt}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(API_URL + '/api/analytics/user', { headers: getAuthHeaders() });
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-40 mb-6" />
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!data) return null;

  const planType = data.plan_type || 'free';
  const isPremium = data.is_premium || false;
  const streakCount = data.streak ? data.streak.current : 0;
  const streakLabel = data.streak ? data.streak.label : '0 weeks';
  const credScore = data.credibility ? data.credibility.score : 100;
  const credStatus = data.credibility ? data.credibility.status : 'Excellent';
  const ov = data.overview || {};
  const goals = data.goals || {};
  const groups = data.groups || {};

  const planBadgeCls =
    planType === 'pro'
      ? 'bg-violet-100 text-violet-700'
      : planType === 'premium'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-slate-100 text-slate-600';

  const PlanIcon = planType === 'pro' ? Crown : planType === 'premium' ? Zap : Shield;

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="analytics-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="analytics-back-btn"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Analytics</h1>
            <p className="text-xs text-slate-500">Your financial insights</p>
          </div>
          <Badge className={'text-xs ' + planBadgeCls} data-testid="analytics-plan-badge">
            <PlanIcon size={12} className="mr-1" />
            {planType.toUpperCase()}
          </Badge>
        </div>

        {/* Streak + Credibility */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="border-0 shadow-sm" data-testid="streak-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-orange-100">
                  <Flame size={16} className="text-orange-500" />
                </div>
                <span className="text-xs font-medium text-slate-500">Streak</span>
              </div>
              <p className="text-2xl font-black text-slate-900">{streakCount}</p>
              <p className="text-[10px] text-slate-400">{streakLabel}</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="credibility-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-emerald-100">
                  <Shield size={16} className="text-emerald-500" />
                </div>
                <span className="text-xs font-medium text-slate-500">Score</span>
              </div>
              <p className="text-2xl font-black text-slate-900">{credScore}</p>
              <p className="text-[10px] text-slate-400">{credStatus}</p>
            </CardContent>
          </Card>
        </div>

        {/* Financial Overview */}
        <Card className="border-0 shadow-sm mb-4" data-testid="financial-overview">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-slate-700 mb-3">Financial Overview</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Contributed</p>
                <p className="text-base font-bold text-slate-900">{fmt(ov.total_contributed)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Payouts</p>
                <p className="text-base font-bold text-emerald-600">{fmt(ov.total_payouts)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Saved in Goals</p>
                <p className="text-base font-bold text-blue-600">{fmt(ov.total_saved_in_goals)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Penalties</p>
                <p className="text-base font-bold text-red-500">{fmt(ov.total_penalties)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Goals + Groups */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="border-0 shadow-sm" data-testid="goals-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-blue-100">
                  <Target size={16} className="text-blue-500" />
                </div>
                <span className="text-xs font-medium text-slate-500">Goals</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-slate-900">{goals.active || 0}</span>
                <span className="text-xs text-slate-400">active</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {goals.completed || 0} completed of {goals.total_goals || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="groups-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-indigo-100">
                  <Users size={16} className="text-indigo-500" />
                </div>
                <span className="text-xs font-medium text-slate-500">Groups</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-slate-900">{groups.active || 0}</span>
                <span className="text-xs text-slate-400">active</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {groups.total || 0} total memberships
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Velocity */}
        {isPremium && data.velocity ? (
          <VelocityChart velocity={data.velocity} />
        ) : (
          <LockedSection title="Savings Velocity" navigate={navigate} />
        )}

        {/* Flow */}
        {isPremium && data.ratio ? (
          <FlowCard ratio={data.ratio} />
        ) : (
          <LockedSection title="Money Flow" navigate={navigate} />
        )}

        <p className="text-center text-[10px] text-slate-400 mt-4 mb-2">app.aegispay.app</p>
      </div>
      <BottomNav />
    </div>
  );
}
